import { BasePresenter } from "@presenters/base/base.presenter";
import { IMainBudget } from '@interfaces/screens/main-budget.interface';
import { ILang, IBudgetFormBasicDataMasters } from "@models/*";
import { useSelector } from "react-redux";
import { BudgetService, IBudgetService } from "@services/proyecta/budget.service";
import { String, Utilities, moment, Enums, Dictionary, Constants} from "@utils";
import { dxDataGridColumn } from "devextreme/ui/data_grid";
import { IDataGridOptions } from "devextreme-react/data-grid";

export class MainBudgetPresenter extends BasePresenter<IMainBudget>{
    private currentLang: ILang;
    private clientBudget: IBudgetService;
    private currentBudget: any;
    public currentLabel : Dictionary<string,string>
    public formMaster: IBudgetFormBasicDataMasters;
    public params: any;

    constructor(view?: IMainBudget) {
        super(view);
        this.currentLang = useSelector(this.context.sessionStore.selectLang);
        this.clientBudget = new BudgetService();
        this.currentBudget = useSelector(this.context.budgetStore.selectCurrentBudget) as any;
        this.currentLabel = useSelector(this.context.sessionStore.selectLabels);
        this.params = useSelector(this.context.sessionStore.selectParams);
        this.formMaster = useSelector(this.context.budgetStore.selectFormMasters);
    }

    public getFieldDisplayName(fieldName: string) {
        if (!Utilities.isNullObject(this.view.state.dataColumns)) {
            const displayName = this.view.state.dataColumns.get(fieldName)?.NombreColumna;
            return displayName;
        }
        return String.Empty;
    }    
    private getLangName(item: any){            
        if (!Utilities.isNullObject(this.currentLang)) {
            switch (this.currentLang.label.toLocaleUpperCase()) {
                case Enums.LangLabels.EN: return item.OCTAVIANombreEn; 
                case Enums.LangLabels.PT: return item.OCTAVIANombrePT;
                case Enums.LangLabels.FR: return item.OCTAVIANombreFR;
                default: return item.Title
            } 
        }        
    }
    private async getCSVByOnDataColumns() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const columns = await this.clientBudget.getBudgetCSVDataColumns(this.currentLang.id);
            this.view.state.setDataColumns(columns);
        }
    }
    private async getCSVByOnDataSource() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const ds = await this.clientBudget.getAllBudgetByOn(this.currentLang.label);
            this.view.state.setDataSource(ds);
        }        
    }    
    public get lastUpdateDate(): any {
        if(!Utilities.isNullEmptyObject(this.currentLabel)){
            let date = new Date();
            date.setHours(1);
            if (this.view.state.dataSource.length > 0) {
                date = this.view.state.dataSource[this.view.state.dataSource.length - 1].Created;
            }
            return this.currentLabel.get("Última Actualización") + " :  " + moment.utc(date).tz(Constants.GeneralFormats.TIME_ZONE).format("L, h:mm a");
        }        
    }

    //#region index 
    public initializeIndex() {
        //this._exampleService.updateExampleSpfr();
        this.dispatcher(this.context.budgetStore.getAllFormMasters());
        this.dispatcherProgress(0);
        this.dispatcherPendingLoad(1);
        this.getIndexDataColumns();
        this.resetCurrentBudget();
    }
    private resetCurrentBudget() {
        if (!Utilities.isNullEmptyObject(this.currentBudget)) {
            this.dispatcher(this.context.budgetStore.resetCurrentBudget());
            this.dispatcherPendingLoad(1);
            this.dispatcherProgress(0);
        }
    }
    private async getIndexDataColumns() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const columns = await this.clientBudget.getBudgetIndexDataColumns(this.currentLang.id);
            this.view.state.setDataColumns(columns);
        }
    }
    public async getIndexDataSource(){
        if(!Utilities.isNullObject(this.currentLang)){
            const ds = await this.clientBudget.getExpandBudgets();
            
            const auxDs = ds.map(item => {
                return {
                    ...item,
                    PPresCodProy: item.CodigoProyecto ? item.CodigoProyecto?.Title : null,
                    IntLine: item.OCTAVIALineaIntervencion ? this.getLangName(item.OCTAVIALineaIntervencion) : null,
                    Region: item.OCTAVIARegion ? this.getLangName(item.OCTAVIARegion) : this.getLangName(item.OCTAVIAPais),
                    PpresType: item.PPresTipoPres ? this.getLangName(item.PPresTipoPres) : null
                }
            });
            this.dispatcher(this.context.sessionStore.actions.setLoadingContainer(false));

            auxDs?.forEach((x: any) => {
                x.canEdit = this.getTotalPermissions(x.Region, this.params, this.formMaster)
                return x
            })
            
            this.view.state.setDataSource(auxDs);
            this.dispatcherProgress(20);
        }
    }
    private get indexColumns(){        
        return <dxDataGridColumn[]>[
            {
                caption: this.getFieldDisplayName("Tipo Presupuesto"),
                dataField: 'PpresType',
                visible: true,
                dataType: 'string',
                alignment: 'center',
                groupIndex: 0,
                sortOrder: 'desc'
            },
            {
                caption: this.getFieldDisplayName("Región"),
                dataField: 'Region',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Año"),
                dataField: 'PPresAnio.Title',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Código de proyecto"),
                dataField: 'PPresCodProy',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Título"),
                dataField: 'Title',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Línea Intervención"),
                dataField: "IntLine",
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                dataField: "botones",
                alignment: "center",
                caption: this.getFieldDisplayName("Acciones"),
                type: "buttons",
                buttons:
                    [{
                        hint: this.getFieldDisplayName('Ver'),
                        icon: 'bulletlist',
                        visible: true,
                        onClick: (e) => {
                            this.context.history.push(`/proyecta/presupuesto/view?Id=${e.row.data.Id}`)
                        }
                    },
                    {
                        hint: this.getFieldDisplayName('Editar'),
                        icon: 'edit',
                        visible: (e) => e.row.data?.canEdit,
                        onClick: (e) => { this.context.history.push(`/proyecta/presupuesto/edit?Id=${e.row.data.Id}`) }
                    }]
            }
        ];
    }

    public getTotalPermissions(country: string, params: any, formMasters: any){
        const TEAM_CENTRAL = params?.[Enums.Params.proyectaCentralTeam]
        const ADMIN_GROUP = params?.[Enums.Params.proyectaAdminModule]

        let countryRegionGroup: string = [...formMasters?.countries, ...formMasters?.regions]?.find((cr: any) => cr.Title === country)?.SGGrupoAD
        let userGroupsId: any[] = this?.currentUser?.userGroups?.map((x: any) => x.id)
        let userGroupsName: any[] = this?.currentUser?.userGroups?.map((x: any) => x.displayName)

        return (userGroupsName?.includes(TEAM_CENTRAL) || userGroupsName?.includes(ADMIN_GROUP) || userGroupsId?.includes(countryRegionGroup))
    }

    private getNextYear() {
        const today = new Date();
        const year = today.getFullYear();
        return (year + 1).toString();
    }

    private get dsIndexOptions() {
        return <IDataGridOptions>{
            columns: this.indexColumns,
            dataSource: this.view.state.dataSource,
            filterPanel: { visible: true },
            filterRow: { visible: true },
            export: { allowExportSelectedData: false },
            grouping: { allowCollapsing: true, autoExpandAll: true },
            columnMinWidth: 100,
            defaultFilterValue: ['PPresAnio.Title', 'contains', this.getNextYear()],
            stateStoring: {
                enabled: true,
                type: "localStorage",
                storageKey: "storage_ppres_index"
            }  
        }
    }
    public get dsProps() {
        return {
            newUrl: "/proyecta/presupuesto/new",
            dataGridOptions: this.dsIndexOptions
        }
    }
    //#endregion

    //#region resume    
    public initializeResume() {
        this.getResumeDataColumns();
        this.getResumeDataSource();
    }
    private async getResumeDataColumns() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const columns = await this.clientBudget.getBudgetResumeDataColumns(this.currentLang.id);
            this.view.state.setDataColumns(columns);
        }
    }
    private async getResumeDataSource() {
        if (!Utilities.isNullObject(this.currentLang)){
            const ds = await this.clientBudget.getAllBudgetSummary(this.currentLang.label);
            this.view.state.setDataSource(ds);
            this.dispatcherProgress(40);
        }
        
    }
    private get resumeColumns() {
        return <dxDataGridColumn[]>[
            {
                caption: this.getFieldDisplayName("AÑO"),
                dataField: 'PPresuAnio',
                visible: true,
                dataType: 'number',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("PaisRegion"),
                dataField: 'PPrespais',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("LineaIntervencion"),
                dataField: 'PPresLineaInter',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("CodigoProyecto"),
                dataField: 'PPresCodProy',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("FFRR"),
                dataField: 'PPresFFRR',
                visible: true,
                dataType: 'number',
                format: '€ #,##0.##; € #,#0'
            },
            {
                caption: this.getFieldDisplayName("FFCC"),
                dataField: 'PPresFFCC',
                visible: true,
                dataType: 'number',
                format: '€ #,##0.##; € #,#0'
            },
            {
                caption: this.getFieldDisplayName("Total"),
                dataField: 'PPresTotalAnio1',
                visible: true,
                dataType: 'number',
                format: '€ #,##0.##; € #,#0'
            }
        ];
    }
    private get dsResumeOptions() {
        return <IDataGridOptions>{
            columns: this.resumeColumns,
            dataSource: this.view.state.dataSource,
            filterPanel: { visible: true },
            filterRow: { visible: true },
            export: { allowExportSelectedData: false },
            grouping: { allowCollapsing: true, autoExpandAll: true },
            columnMinWidth: 100,
            defaultFilterValue: ['PPresuAnio', 'contains', this.getNextYear()],
            stateStoring: {
                enabled: true,
                type: "localStorage",
                storageKey: "storage_ppres_resume"
            } 
        }
    }
    public get dsResumeProps() {
        return {
            dataGridOptions: this.dsResumeOptions
        }
    }
    //#endregion

    //#region detail
    public async initializeDetail() {
        this.getDetailDataColumns();
        this.getDetailDataSource();
        this.dispatcherProgress(50);
    }
    private async getRegions() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const regions = await this.clientBudget.getRegions(this.currentLang.label);
            return regions;
        }
    }
    private async getCountries() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const countries = await this.clientBudget.getCountries(this.currentLang.label);
            return countries;
        }
    }
    private async getDetailDataColumns() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const [bColumns, dColumns] = await Promise.all([this.clientBudget.getBudgetDetailColumns(this.currentLang.id), this.clientBudget.getBudgetBasicDataColumns(this.currentLang.id)]);
            dColumns.keys.forEach(key => {
                if (!bColumns.containsKey(key)) {
                    const value = dColumns.get(key);
                    bColumns.add(key, value);
                }

            });
            this.view.state.setDataColumns(bColumns);
        }
    }
    private async getDetailDataSource() {
        const ds = await this.clientBudget.getAllBudgetAndDetails();
        const [regions, countries] = await Promise.all([this.getRegions(), this.getCountries()]);
        const newDs = ds.map((item: any) => {            
            var regionId: any = null;
            var region: any = null
            if (!Utilities.isNullObject(item.budget) && !Utilities.isNullObject(countries) && !Utilities.isNullObject(regions)) {
                regionId = countries.filter((x: any) => x.Id === item.budget.OCTAVIAPaisId)[0]?.OCTAVIARegionId;
                if (regionId) {
                    region = regions.filter((x: any) => x.Id === regionId)?.[0]?.Title ?? '';
                }
            }    
    
            return {
                ...item,
                region: region ? region : item?.budget?.OCTAVIARegion ? regions?.filter((x:any) => x.Id === item.budget?.OCTAVIARegionId)?.[0]?.Title: null,
                pais: item.budget?.OCTAVIAPais ? this.getLangName(item.budget?.OCTAVIAPais) : null,
                code: item.OCTAVIACodigoProyectoId ? item.OCTAVIACodigoProyecto.Title : item?.budget?.CodigoProyecto?.Title || null,
                intLine: item.budget?.OCTAVIALineaIntervencion ? this.getLangName(item.budget?.OCTAVIALineaIntervencion) : null,
                originCost: item.PPresNatuGasto ? this.getLangName(item.PPresNatuGasto) : null,
                ppresType: item.budget?.PPresTipoPres ? this.getLangName(item.budget?.PPresTipoPres) : null,
                ppresPurpose: item.PPresFinalidad ? this.getLangName(item.PPresFinalidad) : null,
                ppresCECO: item.PPresCeco ? this.getLangName(item.PPresCeco) : null,
                ppresCampaing: item.PPresCampanas ? this.getLangName(item.PPresCampanas) : null,
                ppresDept: item.PPresDepartamento ? this.getLangName(item.PPresDepartamento) : null
            }
        });
        this.view.state.setDataSource(newDs);
    }
    private get detailColumns() {
        return <dxDataGridColumn[]>[
            {
                caption: this.getFieldDisplayName("Tipo Presupuesto"),
                dataField: 'ppresType',
                visible: true,
                dataType: 'string',
                alignment: 'center',
                sortOrder: 'desc'
            },
            {
                caption: this.getFieldDisplayName("Región"),
                dataField: 'region',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("País"),
                dataField: 'pais',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Año"),
                dataField: 'budget.PPresAnio.Title',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Código proyecto"),
                dataField: 'code',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Título de proyecto"),
                dataField: 'budget.Title',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Línea de intervención"),
                dataField: 'intLine',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Finalidad"),
                dataField: 'ppresPurpose',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Identificación financiador"),
                dataField: 'OCTAVIAFinanciadorSap.OCTAVIACodigo',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Código Subvención"),
                dataField: 'PPresCodSubvencion.OCTAVIACodigoSubvencion',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Naturaleza de gasto"),
                dataField: 'originCost',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("CECO"),
                dataField: 'ppresCECO',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Campaña"),
                dataField: 'ppresCampaing',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Código financiador FFRR"),
                dataField: 'PPresCodProyFFCC',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Departamento"),
                dataField: 'ppresDept',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Código PEP"),
                dataField: 'PPresCodPep',
                visible: true,
                dataType: 'string',
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Total Año"),
                dataField: 'PPresTotalAnio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Enero"),
                dataField: 'PPresEnero',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center',

            },
            {
                caption: this.getFieldDisplayName("Febrero"),
                dataField: 'PPresFebrero',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Marzo"),
                dataField: 'PPresMarzo',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Abril"),
                dataField: 'PPresAbril',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Mayo"),
                dataField: 'PPresMayo',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Junio"),
                dataField: 'PPresJunio',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Julio"),
                dataField: 'PPresJulio',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Agosto"),
                dataField: 'PPresAgosto',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Septiembre"),
                dataField: 'PPresSeptiembre',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Octubre"),
                dataField: 'PPresOctubre',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Noviembre"),
                dataField: 'PPresNoviembre',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            },
            {
                caption: this.getFieldDisplayName("Diciembre"),
                dataField: 'PPresDiciembre',
                visible: false,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT,
                alignment: 'center'
            }

        ];
    }
    private get dsDetailOptions() {
        return <IDataGridOptions>{
            columns: this.detailColumns,
            dataSource: this.view.state.dataSource,
            filterPanel: { visible: true },
            filterRow: { visible: true },
            columnMinWidth: 200,
            onToolbarPreparing: null,
            export: { enabled: true, fileName: "Budget Details" },
            defaultFilterValue: ['budget.PPresAnio.Title', 'contains', this.getNextYear()],
            stateStoring: {
                enabled: true,
                type: "localStorage",
                storageKey: "storage_ppres_budget_details"
            } 
        }
    }
    public get dsDetailProps() {
        return {
            dataGridOptions: this.dsDetailOptions
        }
    }
    //#endregion

    //#region csv By Country
    public initializeCSVByCountry() {
        this.getCSVByCountryDataSource();
        this.getCSVByCountryDataColumns();
        this.dispatcherProgress(60);
    }
    private async getCSVByCountryDataSource() {
        if(!Utilities.isNullObject(this.currentLang)){
            const ds = await this.clientBudget.getAllBudgetByCountry(this.currentLang.label);
            this.view.state.setDataSource(ds);
        }
    }
    private async getCSVByCountryDataColumns() {
        if (!Utilities.isNullObject(this.currentLang)) {
            const columns = await this.clientBudget.getBudgetCSVDataColumns(this.currentLang.id);
            this.view.state.setDataColumns(columns);
        }
    }
    private get csvByCountryColumns() {
        return <dxDataGridColumn[]>[
            {
                caption: this.getFieldDisplayName("PPrespais"),
                dataField: 'PPrespais',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresuAnio"),
                dataField: 'PPresuAnio',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresAplicaCodCompanias"),
                dataField: 'PPresEstatico1',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodigoFFCC"),
                dataField: 'PPresCodPep',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresDeptoString"),
                dataField: 'PPresEstatico2',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico1"),
                dataField: 'PPresEstatico3',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodNaturaleza"),
                dataField: 'PPresCodNaturaleza',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresAplicaCodSubvencion"),
                dataField: 'PPresAplicaCodSubvencion',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico2"),
                dataField: 'PPresEstatico4',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodProy"),
                dataField: 'PPresCodProy',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico3"),
                dataField: '',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("ProaRecuperacionCostes"),
                dataField: 'ProaRecuperacionCostes',
                visible: true,
                dataType: 'string',
            },
            {
                caption: this.getFieldDisplayName("PPresEnero"),
                dataField: 'PPresEnero',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresFebrero"),
                dataField: 'PPresFebrero',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresMarzo"),
                dataField: 'PPresMarzo',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresAbril"),
                dataField: 'PPresAbril',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresMayo"),
                dataField: 'PPresMayo',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresJunio"),
                dataField: 'PPresJunio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresJulio"),
                dataField: 'PPresJulio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresAgosto"),
                dataField: 'PPresAgosto',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresSeptiembre"),
                dataField: 'PPresSeptiembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresOctubre"),
                dataField: 'PPresOctubre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresNoviembre"),
                dataField: 'PPresNoviembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresDiciembre"),
                dataField: 'PPresDiciembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            }
        ];
    }
    private get dsCSVByCountryOptions() {
        return <IDataGridOptions>{
            columns: this.csvByCountryColumns,
            dataSource: this.view.state.dataSource,
            filterPanel: { visible: true },
            filterRow: { visible: true },
            onToolbarPreparing: null,
            export: { 
                fileName: "SAP Proyectos", 
                excelFilterEnabled: true, 
                enabled: true
            },
            columnWidth: 50,
            defaultFilterValue: ['PPresuAnio', 'contains', this.getNextYear()]/*,
            stateStoring: {
                enabled: true,
                type: "localStorage",
                storageKey: "storage_ppres_by_country"
            } */
        }
    }
    public get dsCSVByCountryProps() {
        return {
            dataGridOptions: this.dsCSVByCountryOptions
        }
    }
    //#endregion

    //#region CSV By On PEP
    public initializeCSVByOnPEP() {
        this.getCSVByOnDataSource();
        this.getCSVByOnDataColumns();
        this.dispatcherProgress(70);
    }       
    private get csvByOnColumnsPEP() {
        return <dxDataGridColumn[]>[
            {
                caption: this.getFieldDisplayName("PPrespais"),
                dataField: 'PPrespais',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresuAnio"),
                dataField: 'PPresuAnio',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresAplicaCodCompanias"),
                dataField: 'PPresAplicaCodCompanias',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodigoFFCC"),
                dataField: 'PPresCodigoFFCC',
                visible: true,
                dataType: 'string'
            },            
            {
                caption: this.getFieldDisplayName("PPresDeptoString"),
                dataField: 'PPresDeptoString',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico1"),
                dataField: 'PPresEstatico1',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodNaturaleza"),
                dataField: 'PPresCodNaturaleza',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresAplicaCodSubvencion"),
                dataField: 'PPresAplicaCodSubvencion',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico2"),
                dataField: 'PPresEstatico2',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodProy"),
                dataField: 'PPresCodProy',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico3"),
                dataField: 'PPresEstatico3',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("ProaRecuperacionCostes"),
                dataField: 'ProaRecuperacionCostes',
                visible: true,
                dataType: 'string',
            },
            {
                caption: this.getFieldDisplayName("PPresEnero"),
                dataField: 'PPresEnero',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresFebrero"),
                dataField: 'PPresFebrero',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresMarzo"),
                dataField: 'PPresMarzo',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresAbril"),
                dataField: 'PPresAbril',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresMayo"),
                dataField: 'PPresMayo',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresJunio"),
                dataField: 'PPresJunio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresJulio"),
                dataField: 'PPresJulio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresAgosto"),
                dataField: 'PPresAgosto',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresSeptiembre"),
                dataField: 'PPresSeptiembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresOctubre"),
                dataField: 'PPresOctubre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresNoviembre"),
                dataField: 'PPresNoviembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresDiciembre"),
                dataField: 'PPresDiciembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            }
        ];
    }
    private get dsCSVByOnOptionsPEP() {
        return <IDataGridOptions>{
            columns: this.csvByOnColumnsPEP,
            dataSource: this.view.state.dataSource.filter(item => item.PPresTipoOn === "PEP"),
            filterPanel: { visible: true },
            filterRow: { visible: true },
            onToolbarPreparing: null,
            export: { 
                fileName: this.currentLabel.get("SAP ON PEP"), 
                excelFilterEnabled: true, 
                enabled: true
            },
            defaultFilterValue: ['PPresuAnio', 'contains', this.getNextYear()]/*,
            stateStoring: {
                enabled: true,
                type: "localStorage",
                storageKey: "storage_ppres_sap_on_pep"
            } */
        };
    }
    public get dsCSVByOnPropsPEP() {
        return {
            dataGridOptions: this.dsCSVByOnOptionsPEP
        }
    }
    //#endregion

    //#region CSV By On CECO
    public initializeCSVByOnCECO() {
        this.getCSVByOnDataSource();
        this.getCSVByOnDataColumns();
        this.dispatcherProgress(80);
    }       
    private get csvByOnColumnsCECO() {
        return <dxDataGridColumn[]>[
            {
                caption: this.getFieldDisplayName("PPrespais"),
                dataField: 'PPrespais',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresuAnio"),
                dataField: 'PPresuAnio',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresAplicaCodCompanias"),
                dataField: 'PPresAplicaCodCompanias',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCecoString"),
                dataField: 'PPresCecoString',
                visible: true,
                dataType: 'string'
            },            
            {
                caption: this.getFieldDisplayName("PPresFinalidadString"),
                dataField: 'PPresFinalidadString',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico1"),
                dataField: 'PPresEstatico1',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodNaturaleza"),
                dataField: 'PPresCodNaturaleza',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresAplicaCodSubvencion"),
                dataField: 'PPresAplicaCodSubvencion',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico2"),
                dataField: 'PPresEstatico2',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresCodProy"),
                dataField: 'PPresCodProy',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("PPresEstatico3"),
                dataField: 'PPresEstatico3',
                visible: true,
                dataType: 'string'
            },
            {
                caption: this.getFieldDisplayName("ProaRecuperacionCostes"),
                dataField: 'ProaRecuperacionCostes',
                visible: true,
                dataType: 'string',
            },
            {
                caption: this.getFieldDisplayName("PPresEnero"),
                dataField: 'PPresEnero',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresFebrero"),
                dataField: 'PPresFebrero',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresMarzo"),
                dataField: 'PPresMarzo',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresAbril"),
                dataField: 'PPresAbril',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresMayo"),
                dataField: 'PPresMayo',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresJunio"),
                dataField: 'PPresJunio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresJulio"),
                dataField: 'PPresJulio',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresAgosto"),
                dataField: 'PPresAgosto',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresSeptiembre"),
                dataField: 'PPresSeptiembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresOctubre"),
                dataField: 'PPresOctubre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresNoviembre"),
                dataField: 'PPresNoviembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            },
            {
                caption: this.getFieldDisplayName("PPresDiciembre"),
                dataField: 'PPresDiciembre',
                visible: true,
                dataType: 'number',
                format: Constants.GeneralFormats.CURRENCY_FORMAT
            }
        ];
    }
    private get dsCSVByOnOptionsCECO() {
        return <IDataGridOptions>{
            columns: this.csvByOnColumnsCECO,
            dataSource: this.view.state.dataSource.filter(item => item.PPresTipoOn === "CECO"),
            filterPanel: { visible: true },
            filterRow: { visible: true },
            onToolbarPreparing: null,
            export: { 
                fileName: this.currentLabel.get("SAP ON CECO"), 
                excelFilterEnabled: true, 
                enabled: true
            },
            defaultFilterValue: ['PPresuAnio', 'contains', this.getNextYear()]
            //stateStoring: {
            //    enabled: true,
            //    type: "localStorage",
            //    storageKey: "storage_ppres_sap_on_ceco"
            //} 
        };
    }
    public get dsCSVByOnPropsCECO() {
        return {
            dataGridOptions: this.dsCSVByOnOptionsCECO
        }
    }
    //#endregion
}